import { gql, useQuery } from "@apollo/client";
import { faCalendarAlt } from "@fortawesome/pro-light-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  Checkbox,
  FormControlLabel,
  Grid,
  makeStyles,
  Typography,
} from "@material-ui/core";
import {
  AtlasMenuContentItem,
  Button,
  DeprecatedLink,
  Icon,
  Menu,
} from "@resource/atlas";
import { atlasTemplate } from "@resource/atlas/icons";
import { useFlags } from "@resource/client-ffs";
import { EVENT_PLACEHOLDERS } from "@resource/common";
import Loading from "components/legacy/Loading/Loading";
import FeatureFlags from "generated/FeatureFlags";
import {
  GetInterviewTemplates,
  GetInterviewTemplates_currentUserPrisma_currentOrganization_interviewTemplates as InterviewTemplate,
} from "generated/schemaTypes";
import _ from "lodash";
import dynamic from "next/dynamic";
import NextLink from "next/link";
import { useCallback, useMemo } from "react";
import { Descendant } from "slate";
import { SlateToHTML } from "utils/editor";

const RichTextEditor = dynamic(
  () => import("components/legacy/RichTextEditor/RichTextEditor"),
  { loading: () => <Loading />, ssr: false }
);

const useStyles = makeStyles(() => ({
  hideContainer: {
    textAlign: "right",
  },
  checkbox: {
    padding: 0,
  },
  title: {
    marginLeft: "0.4rem",
    whiteSpace: "break-spaces",
    wordBreak: "break-all",
  },
  titleContainer: {
    display: "flex",
  },
}));

const GET_INTERVIEW_TEMPLATES = gql`
  query GetInterviewTemplates {
    currentUserPrisma {
      id
      currentOrganization {
        id
        interviewTemplates(
          where: { status: ACTIVE }
          orderBy: [{ name: asc }]
        ) {
          id
          name
          title
          description
        }
        customer {
          id
          atsIntegration {
            id
            displayName
          }
        }
      }
    }
  }
`;

type InterviewTemplateProps = {
  atsInterviewName: string;
  title: string;
  description: string;
  hidden?: boolean;
  interviewTemplateId?: string | null;
  setTitle(title: string): void;
  setDescription(description: string): void;
  setHidden?: (hidden: boolean) => void;
  setInterviewTemplateId?: (newInterviewTemplateId: string | null) => void;
  showHidden?: boolean;
  showTemplate?: boolean;
};

function InterviewTemplate({
  atsInterviewName,
  title,
  description,
  hidden,
  interviewTemplateId,
  setTitle,
  setDescription,
  setHidden = () => {},
  setInterviewTemplateId = () => {},
  showHidden = true,
  showTemplate = false,
}: InterviewTemplateProps) {
  const styles = useStyles();
  const { data } = useQuery<GetInterviewTemplates>(GET_INTERVIEW_TEMPLATES);
  const {
    [FeatureFlags.NEW_INTERVIEW_TEMPLATES]: newInterviewTemplatesEnabled,
  } = useFlags();

  const interviewTemplates =
    data?.currentUserPrisma?.currentOrganization?.interviewTemplates;
  // to prevent useless re-computations, use undefined if array is empty
  const templates = interviewTemplates?.length ? interviewTemplates : undefined;

  const atsDisplayName =
    data?.currentUserPrisma?.currentOrganization?.customer.atsIntegration
      ?.displayName;

  const selectedInterviewTemplate = _.find(templates, {
    id: interviewTemplateId,
  }) as InterviewTemplate | undefined;

  const removeInterviewTemplate = () => {
    setInterviewTemplateId(null);
  };

  const setInterviewTemplate = useCallback(
    (newInterviewTemplateId: string) => {
      setInterviewTemplateId(newInterviewTemplateId);
      const newInterviewTemplate = _.find(templates, {
        id: newInterviewTemplateId,
      });
      if (!newInterviewTemplate) {
        throw new Error("Something has gone very wrong");
      }
      if (newInterviewTemplate.description) {
        setDescription(
          SlateToHTML(
            newInterviewTemplate.description as Descendant[],
            EVENT_PLACEHOLDERS
          )
        );
      } else {
        setDescription("");
      }
      if (newInterviewTemplate.title) {
        setTitle(
          SlateToHTML(
            newInterviewTemplate.title as Descendant[],
            EVENT_PLACEHOLDERS
          )
        );
      } else {
        setTitle("");
      }
    },
    [setDescription, setInterviewTemplateId, setTitle, templates]
  );
  const isInterviewTemplateSelected =
    selectedInterviewTemplate && newInterviewTemplatesEnabled;

  const menuItems = useMemo(
    () =>
      templates?.map(
        ({ id, name }): AtlasMenuContentItem => ({
          _type: "item",
          children: name,
          key: id,
          size: "compact",
          onClick: () => setInterviewTemplate(id),
        })
      ) ?? [],
    [setInterviewTemplate, templates]
  );

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} md={8} className={styles.titleContainer}>
        <Typography>
          <FontAwesomeIcon icon={faCalendarAlt} size="lg" />
          <strong className={styles.title}>{atsInterviewName}</strong>
        </Typography>
      </Grid>
      {showHidden && (
        <Grid item xs={12} md={4} className={styles.hideContainer}>
          <FormControlLabel
            control={
              <Checkbox
                className={styles.checkbox}
                color="primary"
                checked={hidden}
                onChange={(e) => {
                  setHidden(e.target.checked);
                }}
              />
            }
            label="Hide Interview"
          />
        </Grid>
      )}
      {showTemplate && (
        <Grid item xs={12} md={4}>
          <Menu.Root searchable>
            <Menu.Trigger>
              <Button className="float-right -mt-2.5" isGhost isDropdown>
                {selectedInterviewTemplate?.name ?? "Templates"}
              </Button>
            </Menu.Trigger>
            <Menu.Content items={menuItems} />
          </Menu.Root>
        </Grid>
      )}
      <Grid item xs={12}>
        {isInterviewTemplateSelected && (
          <div className="bg-blue-50 text-blue-900 mb-6 p-4 rounded-lg flex">
            <div className="flex-shrink-0 mr-3 text-blue-500">
              <Icon content={atlasTemplate} />
            </div>
            <div className="flex-grow">
              <div className="text-body-md-heavy mb-2">
                {selectedInterviewTemplate?.name} template applied
              </div>
              <div className="text-body-md mb-3">
                When using a template, the name and description can no longer be
                edited at this level. Users may still override the content via
                the {atsDisplayName} extension.
              </div>
              <div>
                <DeprecatedLink
                  onPress={removeInterviewTemplate}
                  className="pr-3"
                  variant="information"
                >
                  Unlink from template
                </DeprecatedLink>
                <NextLink
                  href={`/templates/interviews?edit=${interviewTemplateId}`}
                  passHref
                >
                  <DeprecatedLink variant="information" target="_blank">
                    Edit template
                  </DeprecatedLink>
                </NextLink>
              </div>
            </div>
          </div>
        )}
        <RichTextEditor
          disabled={!!isInterviewTemplateSelected}
          label="Interview Name Override"
          toolbar="top"
          placeholders={EVENT_PLACEHOLDERS}
          toolbarFormats={{
            placeholder: true,
          }}
          trimEmpty
          onChange={setTitle}
          value={title}
        />
      </Grid>
      <Grid item xs={12}>
        <RichTextEditor
          disabled={!!isInterviewTemplateSelected}
          label="Description"
          toolbar="top"
          onChange={setDescription}
          placeholders={EVENT_PLACEHOLDERS}
          trimEmpty
          height={240}
          value={description}
        />
      </Grid>
    </Grid>
  );
}

export default InterviewTemplate;
